import { AIRCRAFT, AUTOPILOT, CONTROLS, SETTINGS } from '../core/SimConfig.js';
import { clamp, DEG2RAD } from '../utils.js';
import { WeaponMode } from '../weapons/WeaponSystem.js';
import { CameraMode } from '../camera/CameraManager.js';
import LookToSteerPilot from './LookToSteerPilot.js';
import FlightAssist from './FlightAssist.js';
import { ControlScheme, SCHEME_INFO, isBodyLook } from './ControlScheme.js';
import {
  HOLD_ACTIONS, WRECK_ACTIONS, PAUSED_ACTIONS, KEY_ACTIONS, AXIS_KEYS, THROTTLE_KEYS,
  GAMEPAD_BUTTONS, GAMEPAD_RUDDER, GAMEPAD_THROTTLE, JOYSTICK_BUTTONS,
} from './bindings.js';
import {
  findDevice, deviceLabel, defaultJoystickLayout, decodePov, shapeStick, shapeAxis, buttonDown,
} from './DeviceInput.js';
import { isKeyboardLocked } from './keyboardLock.js';

const AXES = ['pitch', 'roll', 'yaw'];
const _stick2 = [0, 0];

// Joystick hat look-points, clockwise from up: [yaw (+ = left), pitch].
const HAT_LOOK = [
  [0, 35], [-45, 20], [-90, 0], [-140, 0], [0, -30], [140, 0], [90, 0], [45, 20],
].map(([y, p]) => [y * DEG2RAD, p * DEG2RAD]);

/**
 * Player input → aircraft controls, for every control scheme.
 *
 * Inputs from each source (keyboard, mouse, gamepad, joystick) resolve into
 * the same small vocabulary of actions (see bindings.js) and three stick axes,
 * and the scheme decides who flies:
 *
 * - **Basic** — the look-to-steer autopilot (LookToSteerPilot) chases the
 *   mouse-look direction. A held WASD/QE key takes that one axis over through
 *   FlightAssist, handing it back a moment after release.
 * - **Everything else** — FlightAssist flies the summed stick: keyboard axes,
 *   plus the virtual mouse stick (Manual mouse) or the device's axes.
 *
 * Head look follows the scheme too: Basic's look is world-space (it *is* the
 * autopilot's target, held level with the horizon), every other scheme's is a
 * body-relative `headYaw`/`headPitch` the cameras hang off the airframe.
 *
 * `poll()` runs every frame, paused or not, so a pad's Menu button can resume;
 * `update()` runs on sim time and drives the surfaces.
 */
export default class Controller {
  constructor({ canvas, aircraft, interactive = true }) {
    this.canvas = canvas;
    this.aircraft = aircraft;
    // When false (start-screen preview) the controller attaches no input
    // listeners and update() is a no-op — the aircraft is left untouched so it
    // sits parked, while the camera classes can still read its (neutral) fields.
    this.interactive = interactive;

    this.scheme = ControlScheme.BASIC;
    this.invertPitch = false;
    this.deadzone = SETTINGS.stickDeadzone;
    this.joystickLayout = null;   // player-assigned axes; null = browser default

    // World-space look (Basic): the autopilot's target and the camera's aim.
    this.lookYaw = 0;
    this.lookPitch = 0;
    // Body-relative head look (every other scheme). Sum of a persistent mouse
    // part and a spring-centred device part (right stick / hat).
    this.headYaw = 0;
    this.headPitch = 0;
    this._mouseHeadYaw = 0;
    this._mouseHeadPitch = 0;
    this._devHeadYaw = 0;
    this._devHeadPitch = 0;
    this._hatYaw = 0;
    this._hatPitch = 0;
    this._hatSlewHeld = 0;     // s the hat has been held while slewing the seeker

    // Manual mouse virtual stick (+x right, +y pull), drawn by GameHUD.
    this.stickX = 0;
    this.stickY = 0;

    this.throttle = 0;
    this.sensitivity = AUTOPILOT.sensitivity;
    this.throttleStep = AUTOPILOT.throttleStep;
    this.isLocked = false;

    this.pilot = new LookToSteerPilot(aircraft);
    this.assist = new FlightAssist();

    // Keyboard flight axes, ramped toward the held keys.
    this._keys = new Set();
    this._kb = { pitch: 0, roll: 0, yaw: 0 };
    // Basic: per-axis keyboard takeover of the autopilot (0 = autopilot, 1 = keys).
    this._takeover = { pitch: { w: 0, idle: 1e9 }, roll: { w: 0, idle: 1e9 }, yaw: { w: 0, idle: 1e9 } };

    // Device state, refreshed by poll().
    this._dev = { pitch: 0, roll: 0, yaw: 0, throttleRate: 0, throttleAbs: -1 };
    this._devRudder = 0;
    this._devicePresent = null; // null = not yet checked
    this._btn = { prefix: '', down: [], time: [], longFired: [] };
    this._hwThrottle = { anchor: -1, engaged: false };

    // Held actions, by source — see bindings.js.
    this._holds = new Map();
    this._paused = false;

    // Weapon system (set by App)
    this.weaponSystem = null;

    // Maverick seeker (set by App) — owns the gimballed seeker + TV display.
    this.maverickSeeker = null;
    // True while the zoom button's "Aim Maverick" mode is engaged (overrides
    // zoom): the mouse/stick slews the seeker, the wheel zooms it. In Basic the
    // autopilot holds the jet level meanwhile; every other scheme leaves the
    // flying to the player.
    this.maverickAiming = false;

    // Camera manager (set by App) — used to route mouse-look while in enemy orbit mode
    this.cameraManager = null;

    // Zoom state (right mouse button) — drives cockpit FOV/head-shift.
    this.zooming = false;

    // Basic freelook (Space): view decoupled from the autopilot's target.
    this.freelook = false;
    this.savedYawOffset = 0;
    this.savedPitchOffset = 0;
    // Manual mouse: Space hands the mouse to the head instead of the stick.
    this._mouseLook = false;

    // Callbacks (set by App)
    this.onToggleCamera = null;
    this.onCycleEnemyCamera = null;
    this.onCycleActionCamera = null;
    this.onTogglePause = null;
    this.onToggleNVG = null;
    this.onPadMenu = null;       // gamepad Menu button
    this.onDeviceStatus = null;  // (text) — connected / waiting messages
    this.onDeviceLost = null;    // the scheme's device went away mid-flight

    // Bind handlers for cleanup
    this._onClick = () => canvas.requestPointerLock();
    this._onPointerLockChange = () => {
      this.isLocked = document.pointerLockElement === canvas;
    };
    this._onMouseMove = (e) => {
      if (!this.isLocked) return;
      // Aim-Maverick mode repurposes the mouse to slew the seeker; player look
      // and the virtual stick are frozen (they resume on exit).
      if (this.maverickAiming) {
        this.maverickSeeker?.slew(e.movementX, e.movementY);
        return;
      }
      // Orbit-camera modes consume the mouse delta; freeze player look so it
      // resumes from where it was when re-entering chase/cockpit.
      const consumed = this.cameraManager?.handleMouseLook(e.movementX, e.movementY, this.sensitivity);
      if (consumed) return;
      // A wreck is orbited in the level framing, whatever the scheme.
      const role = this.aircraft.destroyed ? 'aim' : SCHEME_INFO[this.scheme].mouse;
      if (role === 'aim') {
        this.lookYaw -= e.movementX * this.sensitivity;
        this.lookPitch -= e.movementY * this.sensitivity;
        this.lookPitch = Math.max(-85 * DEG2RAD, Math.min(85 * DEG2RAD, this.lookPitch));
      } else if (role === 'stick' && !this._mouseLook) {
        this._moveStick(e.movementX, e.movementY);
      } else {
        const h = CONTROLS.head;
        this._mouseHeadYaw = clamp(this._mouseHeadYaw - e.movementX * this.sensitivity, -h.yawLimit, h.yawLimit);
        this._mouseHeadPitch = clamp(this._mouseHeadPitch - e.movementY * this.sensitivity, h.pitchDownLimit, h.pitchUpLimit);
      }
    };
    this._onWheel = (e) => {
      // Aim-Maverick mode repurposes the wheel to zoom the seeker optic.
      if (this.maverickAiming) {
        this.maverickSeeker?.zoom(e.deltaY);
        e.preventDefault();
        return;
      }
      // Enemy orbit mode repurposes the wheel to zoom the orbit radius — the
      // throttle is left where the player set it.
      if (this.cameraManager?.handleWheel(e.deltaY)) {
        e.preventDefault();
        return;
      }
      this.throttle += e.deltaY < 0 ? this.throttleStep : -this.throttleStep;
      this.throttle = Math.max(0, Math.min(1, this.throttle));
      this._releaseHwThrottle();
      e.preventDefault();
    };
    this._onKeyDown = (e) => {
      // With the keyboard locked the browser's own Ctrl/Alt shortcuts reach
      // us instead of closing the tab — swallow them.
      if (isKeyboardLocked() && (e.ctrlKey || e.altKey || e.metaKey)) e.preventDefault();
      this._keys.add(e.code);
      if (e.code === 'Space') e.preventDefault();
      const action = KEY_ACTIONS[e.code];
      if (!action) return;
      if (HOLD_ACTIONS.has(action)) this._setHold(action, `k:${e.code}`, true);
      else if (!e.repeat) this._press(action);
    };
    this._onKeyUp = (e) => {
      this._keys.delete(e.code);
      const action = KEY_ACTIONS[e.code];
      if (action && HOLD_ACTIONS.has(action)) this._setHold(action, `k:${e.code}`, false);
    };
    // Keys held as the window loses focus never see their keyup.
    this._onBlur = () => {
      this._keys.clear();
      for (const [action, sources] of this._holds) {
        for (const src of [...sources]) if (src.startsWith('k:') || src.startsWith('m:')) this._setHold(action, src, false);
      }
    };

    this._onMouseDown = (e) => {
      if (e.button === 0) { if (this.isLocked) this._setHold('fire', 'm:0', true); }
      if (e.button === 1) this._press('weapon'); // middle mouse
      if (e.button === 2) this._setHold('zoom', 'm:2', true);
    };
    this._onMouseUp = (e) => {
      if (e.button === 0) this._setHold('fire', 'm:0', false);
      if (e.button === 2) this._setHold('zoom', 'm:2', false);
    };
    this._onContextMenu = (e) => e.preventDefault();

    this._inputEnabled = false;
    if (this.interactive) this.enableInput();
  }

  // ── Scheme ──────────────────────────────────────────────────────────────────

  /** True when the camera hangs off the airframe (every scheme but Basic). */
  get bodyLook() { return isBodyLook(this.scheme); }

  /** Pointer lock is only needed to fly when the mouse is a flight control. */
  get needsPointerLock() { return !SCHEME_INFO[this.scheme].device; }

  /** Whether the HUD should be drawing the enemy target markers. */
  get highlightTargets() {
    return this.freelook || this._holdActive('highlight')
      || (this.scheme !== ControlScheme.BASIC && this._holdActive('look'));
  }

  /** Manual mouse stick is live (drawn on the HUD). */
  get mouseStickActive() {
    return this.scheme === ControlScheme.MOUSE && !this._mouseLook && !this.maverickAiming;
  }

  /**
   * Switch control scheme, live. Going *to* Basic re-aims the autopilot at the
   * current nose so it doesn't haul the jet back to a look direction left over
   * from before; leaving Basic hands the assist the autopilot's elevator so the
   * pitch trim carries over. The cameras ease between their two frames on
   * their own (see CockpitCamera/ChaseCamera `_frameBlend`).
   */
  setScheme(scheme) {
    if (!SCHEME_INFO[scheme]) scheme = ControlScheme.BASIC;
    if (scheme === this.scheme) return;
    this.scheme = scheme;
    this.freelook = false;
    this._mouseLook = false;
    this.stickX = this.stickY = 0;
    this._mouseHeadYaw = this._mouseHeadPitch = 0;
    this._devHeadYaw = this._devHeadPitch = 0;
    this._hatYaw = this._hatPitch = 0;
    for (const a of AXES) this._takeover[a].w = 0;
    if (scheme === ControlScheme.BASIC) {
      this.resetLookToNose();
    } else {
      this.assist.sync(this.aircraft.elevator);
    }
    this._devicePresent = null;
    this._clearDeviceHolds();
  }

  /** Point Basic's world look straight down the nose. */
  resetLookToNose() {
    const fwd = this.aircraft.forwardVector;
    this.lookYaw = Math.atan2(-fwd.x, -fwd.z);
    this.lookPitch = clamp(Math.asin(clamp(fwd.y, -1, 1)), -85 * DEG2RAD, 85 * DEG2RAD);
  }

  /** Centre every manual control (stick, head, trim) — e.g. after a teleport. */
  recenter() {
    this.freelook = false;
    this.stickX = this.stickY = 0;
    this._mouseHeadYaw = this._mouseHeadPitch = 0;
    this.assist.reset(!this.aircraft.onGround);
  }

  // ── Input wiring ────────────────────────────────────────────────────────────

  /**
   * Attach input listeners and begin driving the aircraft. Called from the
   * constructor for interactive sessions, or later by App when promoting the
   * (input-disabled) start-screen preview into a playable scenario.
   */
  enableInput() {
    if (this._inputEnabled) return;
    this._inputEnabled = true;
    this.interactive = true;
    const canvas = this.canvas;
    canvas.addEventListener('click', this._onClick);
    document.addEventListener('pointerlockchange', this._onPointerLockChange);
    document.addEventListener('mousemove', this._onMouseMove);
    document.addEventListener('mousedown', this._onMouseDown);
    document.addEventListener('mouseup', this._onMouseUp);
    canvas.addEventListener('contextmenu', this._onContextMenu);
    canvas.addEventListener('wheel', this._onWheel, { passive: false });
    document.addEventListener('keydown', this._onKeyDown);
    document.addEventListener('keyup', this._onKeyUp);
    window.addEventListener('blur', this._onBlur);
  }

  // ── Actions ─────────────────────────────────────────────────────────────────

  _holdActive(action) {
    const s = this._holds.get(action);
    return !!s && s.size > 0;
  }

  _setHold(action, source, down) {
    let sources = this._holds.get(action);
    if (!sources) { sources = new Set(); this._holds.set(action, sources); }
    const was = sources.size > 0;
    if (down) {
      // A wreck only keeps its view controls; a paused sim takes nothing new.
      if (this.aircraft.destroyed && !WRECK_ACTIONS.has(action)) return;
      if (this._paused && !PAUSED_ACTIONS.has(action)) return;
      sources.add(source);
    } else {
      sources.delete(source);
    }
    const now = sources.size > 0;
    if (now !== was) this._onHoldChange(action, now);
  }

  _onHoldChange(action, active) {
    const a = this.aircraft;
    switch (action) {
      case 'brake': a.airbrakeInput = active ? 1 : 0; break;
      case 'flares': a.countermeasures.firing = active; break;
      case 'fire': active ? this._pressFire() : (this.weaponSystem && (this.weaponSystem.firing = false)); break;
      case 'zoom': active ? this._pressZoom() : this._releaseZoom(); break;
      case 'look':
        if (this.scheme === ControlScheme.BASIC) active ? this._startFreelook() : (this.freelook = false);
        else if (this.scheme === ControlScheme.MOUSE) this._mouseLook = active;
        break;
      default: break; // 'highlight' is read through highlightTargets
    }
  }

  _press(action) {
    if (this.aircraft.destroyed && !WRECK_ACTIONS.has(action)) return;
    if (this._paused && !PAUSED_ACTIONS.has(action)) return;
    const a = this.aircraft;
    switch (action) {
      case 'engine': a.toggleEngine(); break;
      case 'flaps': a.flapStage = (a.flapStage + 1) % 3; break;
      case 'gear':
        // Block gear changes while on the ground (no retraction, and no
        // extending after a belly landing). Uses isGrounded, not the raw
        // onGround flag, which flickers off once a gear-up airframe settles.
        if (!a.isGrounded) a.gearTarget = a.gearTarget === 0 ? 1 : 0;
        break;
      case 'weapon': this.weaponSystem?.cycleMode(); break;
      case 'nvg': this.onToggleNVG?.(); break;
      case 'cameraToggle': this.onToggleCamera?.(); break;
      case 'enemyCamera': this.onCycleEnemyCamera?.(); break;
      case 'actionCamera': this.onCycleActionCamera?.(); break;
      case 'pause': this.onTogglePause?.(); break;
      case 'menu': this.onPadMenu?.(); break;
      default: break;
    }
  }

  _startFreelook() {
    this.freelook = true;
    // Save angular offset between look direction and current aircraft heading
    const fwd = this.aircraft.forwardVector;
    const aircraftYaw = Math.atan2(-fwd.x, -fwd.z);
    const aircraftPitch = Math.asin(Math.max(-1, Math.min(1, fwd.y)));
    this.savedYawOffset = this.lookYaw - aircraftYaw;
    this.savedPitchOffset = this.lookPitch - aircraftPitch;
  }

  _pressFire() {
    const ws = this.weaponSystem;
    if (!ws?.hasWeaponSelected || this.aircraft.destroyed) return;
    if (ws.mode === WeaponMode.AIM9) {
      ws.tryFireAIM9(this.aircraft);
    } else if (ws.mode === WeaponMode.AGM65) {
      // One-shot launch — the seeker only fires while aiming and in range.
      this.maverickSeeker?.fire(this.aircraft);
    } else {
      ws.firing = true;
    }
  }

  _pressZoom() {
    // The zoom button engages "Aim Maverick" mode when a usable Maverick is
    // selected (overriding the normal zoom), else it zooms.
    if (!this.aircraft.destroyed && this.weaponSystem?.mode === WeaponMode.AGM65 && this.maverickSeeker?.canAim) {
      this.maverickAiming = true;
      this.maverickSeeker.enterAim();
    } else {
      this.zooming = true;
    }
  }

  _releaseZoom() {
    if (this.maverickAiming) {
      this.maverickAiming = false;
      this.maverickSeeker?.exitAim();
    }
    this.zooming = false;
  }

  _moveStick(dx, dy) {
    const gain = CONTROLS.mouseStick.gain * (this.sensitivity / AUTOPILOT.sensitivity);
    // Mouse back (dy > 0) pulls, like drawing a stick toward you.
    this.stickX += dx * gain;
    this.stickY += (this.invertPitch ? -dy : dy) * gain;
    const m = Math.hypot(this.stickX, this.stickY);
    if (m > 1) { this.stickX /= m; this.stickY /= m; }
  }

  _releaseHwThrottle() {
    this._hwThrottle.engaged = false;
    this._hwThrottle.anchor = -1;
  }

  // ── Devices ─────────────────────────────────────────────────────────────────

  /**
   * Read the scheme's device. Runs every frame, paused or not (only the Menu
   * button acts while paused), ahead of update().
   */
  poll(dt, paused = false) {
    if (!this.interactive) return;
    this._paused = paused;
    const dev = this._dev;
    dev.pitch = dev.roll = dev.yaw = dev.throttleRate = 0;
    dev.throttleAbs = -1;
    this._devHeadYaw = this._devHeadPitch = 0;

    const kind = SCHEME_INFO[this.scheme].device;
    if (!kind) return;
    const pad = findDevice(kind);
    const present = !!pad;
    if (present !== this._devicePresent) {
      const name = kind === 'gamepad' ? 'GAMEPAD' : 'JOYSTICK';
      if (present) {
        this.onDeviceStatus?.(`${name}: ${deviceLabel(pad).toUpperCase()}`);
      } else if (this._devicePresent) {
        this._clearDeviceHolds();
        this.onDeviceLost?.(`${name} DISCONNECTED`);
      } else {
        this.onDeviceStatus?.(`${name}: PRESS ANY BUTTON`);
      }
      this._devicePresent = present;
      this._btn = { prefix: '', down: [], time: [], longFired: [] };
    }
    if (!pad) return;
    if (kind === 'gamepad') this._readGamepad(pad, dt);
    else this._readJoystick(pad, dt);
  }

  _readGamepad(pad, dt) {
    const cfg = CONTROLS.gamepad;
    const ax = pad.axes;
    const b = pad.buttons;
    const dev = this._dev;

    shapeStick(ax[0] || 0, ax[1] || 0, this.deadzone, cfg.expo, _stick2);
    dev.roll = _stick2[0];
    // Stick back (down, +y) pulls.
    dev.pitch = this.invertPitch ? -_stick2[1] : _stick2[1];

    shapeStick(ax[2] || 0, ax[3] || 0, this.deadzone, 0, _stick2);
    if (this.maverickAiming) {
      this.maverickSeeker?.slew(_stick2[0] * cfg.slewRate * dt, _stick2[1] * cfg.slewRate * dt);
    } else {
      this._setDeviceHead(_stick2[0], _stick2[1]);
    }

    // Bumpers are digital: ramp them like keys.
    const rTarget = (buttonDown(b[GAMEPAD_RUDDER.right]) ? 1 : 0) - (buttonDown(b[GAMEPAD_RUDDER.left]) ? 1 : 0);
    this._devRudder = this._ramp(this._devRudder, rTarget, cfg.rudderRamp, cfg.rudderRamp * 1.5, dt);
    dev.yaw = this._devRudder;

    // D-pad up/down: throttle, or the seeker's zoom while aiming.
    const up = buttonDown(b[GAMEPAD_THROTTLE.up]);
    const down = buttonDown(b[GAMEPAD_THROTTLE.down]);
    if (this.maverickAiming) {
      if (up && !this._btn.down[GAMEPAD_THROTTLE.up]) this.maverickSeeker?.zoom(-1);
      if (down && !this._btn.down[GAMEPAD_THROTTLE.down]) this.maverickSeeker?.zoom(1);
    } else {
      dev.throttleRate = ((up ? 1 : 0) - (down ? 1 : 0)) * cfg.throttleRate;
    }
    this._btn.down[GAMEPAD_THROTTLE.up] = up;
    this._btn.down[GAMEPAD_THROTTLE.down] = down;

    this._processButtons(b, GAMEPAD_BUTTONS, 'g', cfg.holdTime, dt);
  }

  _readJoystick(pad, dt) {
    const cfg = CONTROLS.joystick;
    const layout = this.joystickLayout || defaultJoystickLayout();
    const ax = pad.axes;
    const dev = this._dev;
    const read = (a) => (a && ax[a.i] !== undefined ? ax[a.i] * a.s : 0);

    shapeStick(read(layout.roll), read(layout.pitch), this.deadzone, cfg.expo, _stick2);
    dev.roll = _stick2[0];
    dev.pitch = _stick2[1];
    dev.yaw = shapeAxis(read(layout.yaw), Math.max(this.deadzone, cfg.rudderDeadzone), cfg.expo);

    // Slider throttle: it sets an absolute position, while the wheel and keys
    // nudge. It takes over only once it's actually moved, and gives way again
    // the moment the wheel or keys are used — so a slider parked at idle
    // can't fight a wheel setting.
    const hw = clamp((read(layout.throttle) + 1) / 2, 0, 1);
    const t = this._hwThrottle;
    if (t.anchor < 0) t.anchor = hw;
    if (!t.engaged && Math.abs(hw - t.anchor) > cfg.throttleTakeover) t.engaged = true;
    if (t.engaged) dev.throttleAbs = hw;

    // Hat: snap looks, or the seeker slew while aiming. The hat is digital, so
    // the slew starts fine and ramps up while held — a tap nudges, a hold travels.
    const dir = layout.hat ? decodePov(ax[layout.hat.i]) : -1;
    if (this.maverickAiming) {
      if (dir >= 0) {
        this._hatSlewHeld += dt;
        const ramp = Math.min(this._hatSlewHeld / cfg.slewRampTime, 1);
        const rate = cfg.slewRate + (cfg.slewRateMax - cfg.slewRate) * ramp * ramp;
        const ang = dir * Math.PI / 4;
        this.maverickSeeker?.slew(Math.sin(ang) * rate * dt, -Math.cos(ang) * rate * dt);
      } else {
        this._hatSlewHeld = 0;
      }
      this._hatYaw = this._hatPitch = 0;
    } else {
      this._hatSlewHeld = 0;
      const [ty, tp] = dir >= 0 ? HAT_LOOK[dir] : [0, 0];
      const k = 1 - Math.exp(-cfg.hatLookRate * dt);
      this._hatYaw += (ty - this._hatYaw) * k;
      this._hatPitch += (tp - this._hatPitch) * k;
    }
    this._devHeadYaw = this._hatYaw;
    this._devHeadPitch = this._hatPitch;

    this._processButtons(pad.buttons, JOYSTICK_BUTTONS, 'j', CONTROLS.gamepad.holdTime, dt);
  }

  /** Spring-centred analogue look: the stick's deflection is the head angle. */
  _setDeviceHead(x, y) {
    const h = CONTROLS.head;
    this._devHeadYaw = -x * h.stickYaw;
    this._devHeadPitch = y < 0 ? -y * h.stickPitchUp : y * h.stickPitchDown;
  }

  /** Edge-detect a button table into press/hold actions (see bindings.js). */
  _processButtons(buttons, table, prefix, holdTime, dt) {
    const st = this._btn;
    for (const key in table) {
      const i = +key;
      const bind = table[key];
      const down = buttonDown(buttons[i]);
      const was = !!st.down[i];
      if (bind.hold) {
        if (down !== was) this._setHold(bind.hold, `${prefix}:${i}`, down);
      } else if (!bind.long) {
        if (down && !was) this._press(bind.tap);
      } else {
        if (down && !was) { st.time[i] = 0; st.longFired[i] = false; }
        if (down) {
          st.time[i] += dt;
          if (!st.longFired[i] && st.time[i] >= holdTime) { st.longFired[i] = true; this._press(bind.long); }
        } else if (was && !st.longFired[i] && bind.tap) {
          this._press(bind.tap);
        }
      }
      st.down[i] = down;
    }
  }

  _clearDeviceHolds() {
    for (const [action, sources] of this._holds) {
      for (const src of [...sources]) if (src.startsWith('g:') || src.startsWith('j:')) this._setHold(action, src, false);
    }
    this._devRudder = 0;
    this._releaseHwThrottle();
  }

  // ── Per-frame flight update ─────────────────────────────────────────────────

  _ramp(v, target, rateUp, rateDown, dt) {
    // Toward a non-zero target at rateUp; back to (and through) centre at rateDown.
    const rate = target !== 0 && Math.sign(target) === Math.sign(v || target) ? rateUp : rateDown;
    const step = rate * dt;
    return Math.abs(target - v) <= step ? target : v + Math.sign(target - v) * step;
  }

  _updateKeyboardAxes(dt) {
    const cfg = CONTROLS.keyboard;
    for (const axis of AXES) {
      let target = 0;
      for (const code of this._keys) {
        const bind = AXIS_KEYS[code];
        if (bind && bind[0] === axis) target += bind[1];
      }
      this._kb[axis] = this._ramp(this._kb[axis], clamp(target, -1, 1), cfg.rampUp, cfg.rampDown, dt);
    }
  }

  _axisKeyHeld(axis) {
    for (const code of this._keys) {
      const bind = AXIS_KEYS[code];
      if (bind && bind[0] === axis) return true;
    }
    return false;
  }

  _updateThrottle(dt) {
    let rate = this._dev.throttleRate;
    if (isKeyboardLocked()) {
      for (const code of this._keys) {
        const dir = THROTTLE_KEYS[code];
        if (dir) rate += dir * CONTROLS.keyboard.throttleRate;
      }
    }
    if (rate !== 0) {
      this.throttle = clamp(this.throttle + rate * dt, 0, 1);
      this._releaseHwThrottle();
    } else if (this._dev.throttleAbs >= 0) {
      this.throttle = this._dev.throttleAbs;
      this._hwThrottle.anchor = this._dev.throttleAbs;
    }
  }

  _updateHead(dt) {
    // Manual mouse: letting go of Space eases the view back down the nose.
    if (this.scheme === ControlScheme.MOUSE && !this._mouseLook) {
      const k = Math.exp(-CONTROLS.head.returnRate * dt);
      this._mouseHeadYaw *= k;
      this._mouseHeadPitch *= k;
    }
    const h = CONTROLS.head;
    this.headYaw = clamp(this._mouseHeadYaw + this._devHeadYaw, -h.yawLimit, h.yawLimit);
    this.headPitch = clamp(this._mouseHeadPitch + this._devHeadPitch, h.pitchDownLimit, h.pitchUpLimit);
  }

  update(dt = 1 / 60) {
    if (!this.interactive) return;
    const aircraft = this.aircraft;

    // Destroyed wreck: stop driving the flight controls and cease any fire.
    if (aircraft.destroyed) {
      if (this.weaponSystem) this.weaponSystem.firing = false;
      return;
    }

    // Drop Aim-Maverick mode if it stops being usable mid-hold (rack emptied,
    // or the player cycled away from the Maverick).
    if (this.maverickAiming && !this.maverickSeeker?.canAim) {
      this.maverickAiming = false;
      this.maverickSeeker?.exitAim();
    }

    this._updateKeyboardAxes(dt);
    this._updateThrottle(dt);
    aircraft.throttle = this.throttle;
    this._updateHead(dt);

    // Basic flies at the roll power its autopilot was tuned for; the manual
    // schemes get the airframe's full roll authority.
    aircraft.rollAuthorityScale = this.scheme === ControlScheme.BASIC
      ? AUTOPILOT.rollAuthority / AIRCRAFT.controlAuthority.roll
      : 1;

    const kb = this._kb;
    let out;
    if (this.scheme === ControlScheme.BASIC) {
      out = this._updateBasic(dt);
    } else {
      const mouse = this.scheme === ControlScheme.MOUSE;
      const dz = CONTROLS.mouseStick.deadzone;
      const ms = mouse ? shapeStick(this.stickX, this.stickY, dz, 0, _stick2) : null;
      out = this.assist.update(dt, aircraft, {
        pitch: clamp(kb.pitch + this._dev.pitch + (ms ? ms[1] : 0), -1, 1),
        roll: clamp(kb.roll + this._dev.roll + (ms ? ms[0] : 0), -1, 1),
        yaw: clamp(kb.yaw + this._dev.yaw, -1, 1),
      });
    }
    aircraft.elevator = out.elevator;
    aircraft.ailerons = out.ailerons;
    aircraft.rudder = out.rudder;
  }

  /**
   * Basic: the autopilot flies, and a held WASD/QE key takes its axis over.
   * Each axis blends to the keys over `takeover.blendIn` and back to the
   * autopilot `holdAfter` seconds after release, over `blendOut` — at which
   * point the autopilot resumes chasing wherever the mouse is looking.
   */
  _updateBasic(dt) {
    const aircraft = this.aircraft;
    const gunZoom = this.zooming
      && this.cameraManager?.mode === CameraMode.COCKPIT
      && this.weaponSystem?.mode === WeaponMode.GAU8;

    // In freelook mode, maintain the saved angular offset from current heading
    let yaw, pitch;
    if (this.maverickAiming) {
      // Aim-Maverick: command wings-level flight on the current heading so the
      // seeker has a stable platform. The mouse drives the seeker, not the jet.
      const fwd = aircraft.forwardVector;
      yaw = Math.atan2(-fwd.x, -fwd.z);
      pitch = 0;
    } else if (this.freelook) {
      const fwd = aircraft.forwardVector;
      yaw = Math.atan2(-fwd.x, -fwd.z) + this.savedYawOffset;
      pitch = Math.asin(Math.max(-1, Math.min(1, fwd.y))) + this.savedPitchOffset;
    } else {
      yaw = this.lookYaw;
      pitch = this.lookPitch;
    }
    const ap = this.pilot.update(dt, yaw, pitch, gunZoom);

    // Takeover weights. On the ground A/D steer (through the assist's rudder),
    // so a held roll key claims the yaw axis there too.
    const cfg = CONTROLS.takeover;
    const groundSteer = aircraft.onGround && this._axisKeyHeld('roll');
    let any = false;
    for (const axis of AXES) {
      const t = this._takeover[axis];
      const held = this._axisKeyHeld(axis) || (axis === 'yaw' && groundSteer);
      t.idle = held ? 0 : t.idle + dt;
      const target = held || t.idle < cfg.holdAfter ? 1 : 0;
      t.w = target > t.w
        ? Math.min(1, t.w + dt / cfg.blendIn)
        : Math.max(0, t.w - dt / cfg.blendOut);
      if (t.w > 0) any = true;
    }

    // Keep the assist warm and trimmed to the autopilot's elevator while it
    // doesn't own pitch, so a takeover starts bumpless.
    if (this._takeover.pitch.w === 0) this.assist.sync(ap.elevator);
    const kb = this._kb;
    const as = this.assist.update(dt, aircraft, { pitch: kb.pitch, roll: kb.roll, yaw: kb.yaw },
      CONTROLS.takeover.rollRateMax);
    if (!any) return ap;

    const wp = this._takeover.pitch.w, wr = this._takeover.roll.w, wy = this._takeover.yaw.w;
    const out = this._basicOut || (this._basicOut = { elevator: 0, ailerons: 0, rudder: 0 });
    out.elevator = ap.elevator + (as.elevator - ap.elevator) * wp;
    out.ailerons = ap.ailerons + (as.ailerons - ap.ailerons) * wr;
    out.rudder = ap.rudder + (as.rudder - ap.rudder) * wy;
    return out;
  }

  dispose() {
    this._inputEnabled = false;
    this.interactive = false;
    this.canvas.removeEventListener('click', this._onClick);
    document.removeEventListener('pointerlockchange', this._onPointerLockChange);
    document.removeEventListener('mousemove', this._onMouseMove);
    document.removeEventListener('mousedown', this._onMouseDown);
    document.removeEventListener('mouseup', this._onMouseUp);
    this.canvas.removeEventListener('contextmenu', this._onContextMenu);
    this.canvas.removeEventListener('wheel', this._onWheel);
    document.removeEventListener('keydown', this._onKeyDown);
    document.removeEventListener('keyup', this._onKeyUp);
    window.removeEventListener('blur', this._onBlur);
  }
}
